import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabase/client';
import type { Database } from '../../types/supabase';
import { formatOAuthInitError, type OAuthProviderId } from './oauthErrors';

export type ProfileRow = Database['public']['Tables']['profiles']['Row'];

type OAuthProvider = OAuthProviderId;

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  profile: ProfileRow | null;
  initialized: boolean;
  profileLoading: boolean;
  refreshProfile: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUpWithEmail: (
    email: string,
    password: string
  ) => Promise<{
    error: Error | null;
    session: Session | null;
    /** True when Supabase obfuscates an already-registered email (no confirmation mail is sent). */
    alreadyRegistered: boolean;
    /** True when a new account was created but email confirmation is still required. */
    needsEmailConfirmation: boolean;
  }>;
  signInWithOAuth: (provider: OAuthProvider) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
};

const POST_OAUTH_REDIRECT_KEY = 'newrotune_post_auth_redirect';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function setPostAuthRedirect(path: string): void {
  try {
    sessionStorage.setItem(POST_OAUTH_REDIRECT_KEY, path);
  } catch {
    /* ignore */
  }
}

export function consumePostAuthRedirect(): string | null {
  try {
    const v = sessionStorage.getItem(POST_OAUTH_REDIRECT_KEY);
    sessionStorage.removeItem(POST_OAUTH_REDIRECT_KEY);
    return v && v.startsWith('/') ? v : null;
  } catch {
    return null;
  }
}

async function fetchProfile(userId: string): Promise<ProfileRow | null> {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
  if (error) {
    console.warn('profiles fetch failed', error.message);
    return null;
  }
  return data;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  const refreshProfile = useCallback(async () => {
    const uid = session?.user?.id;
    if (!uid) {
      setProfile(null);
      return;
    }
    setProfileLoading(true);
    try {
      const row = await fetchProfile(uid);
      setProfile(row);
    } finally {
      setProfileLoading(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (cancelled) return;
      setSession(s);
      setUser(s?.user ?? null);
      setInitialized(true);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    setProfileLoading(true);
    fetchProfile(uid).then((row) => {
      if (!cancelled) setProfile(row);
      if (!cancelled) setProfileLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    return { error: error ? new Error(error.message) : null };
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    const emailRedirectTo = `${window.location.origin}/auth/callback`;
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo }
    });

    if (error) {
      return {
        error: new Error(error.message),
        session: null,
        alreadyRegistered: /already registered|already been registered|email.*exists/i.test(error.message),
        needsEmailConfirmation: false
      };
    }

    const identities = data.user?.identities ?? [];
    // With "Confirm email" enabled, duplicate signups return a fake user with empty identities
    // and no session — and typically no confirmation email is delivered.
    const alreadyRegistered = Boolean(data.user) && !data.session && identities.length === 0;
    const needsEmailConfirmation = Boolean(data.user) && !data.session && identities.length > 0;

    return {
      error: null,
      session: data.session ?? null,
      alreadyRegistered,
      needsEmailConfirmation
    };
  }, []);

  const signInWithOAuth = useCallback(async (provider: OAuthProvider) => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    // Azure: Supabase requires the `email` scope so Entra ID returns a usable email claim.
    const oauthOptions =
      provider === 'azure'
        ? { redirectTo: redirectUrl, skipBrowserRedirect: true as const, scopes: 'email' }
        : { redirectTo: redirectUrl, skipBrowserRedirect: true as const };
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: oauthOptions
    });
    if (error) {
      return { error: formatOAuthInitError(error, provider) };
    }
    if (!data.url) {
      return { error: new Error('Could not start sign-in') };
    }
    window.location.assign(data.url);
    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      profile,
      initialized,
      profileLoading,
      refreshProfile,
      signInWithEmail,
      signUpWithEmail,
      signInWithOAuth,
      signOut
    }),
    [
      session,
      user,
      profile,
      initialized,
      profileLoading,
      refreshProfile,
      signInWithEmail,
      signUpWithEmail,
      signInWithOAuth,
      signOut
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
